import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Rocket, Package, Trash2, Bell, ChevronRight, Check,
  X, ArrowRight, Sparkles
} from "lucide-react";

const STEPS = [
  { key: "welcome", icon: Rocket, title: "Bienvenue sur PHARMA PÉRIMÉS", description: "Découvrez la plateforme et commencez à optimiser votre gestion pharmaceutique.", action: null, color: "text-sky-400", bg: "bg-sky-500/15" },
  { key: "import_products", icon: Package, title: "Importez vos produits", description: "Ajoutez votre inventaire manuellement, par CSV, Excel ou PDF. C'est la base de votre suivi.", action: "/import", actionLabel: "Importer mes produits", color: "text-emerald-400", bg: "bg-emerald-500/15" },
  { key: "first_destruction", icon: Trash2, title: "Votre première destruction", description: "Sélectionnez vos produits périmés et soumettez une demande de destruction à CMES.", action: "/destructions", actionLabel: "Demander une destruction", color: "text-amber-400", bg: "bg-amber-500/15" },
  { key: "configure_alerts", icon: Bell, title: "Configurez vos alertes", description: "Définissez vos seuils ITPP pour recevoir des alertes automatiques par SMS et WhatsApp.", action: "/monthly", actionLabel: "Configurer mes alertes", color: "text-violet-400", bg: "bg-violet-500/15" },
];

const OnboardingWizard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
      if (res.data.dismissed || res.data.completed) setDismissed(true);
      else {
        const idx = STEPS.findIndex(s => !res.data.steps?.[s.key]);
        setCurrentStep(idx >= 0 ? idx : STEPS.length - 1);
      }
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const completeStep = async (key) => {
    try {
      await axios.post(`${API}/onboarding/step/${key}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await load();
    } catch { /* ignore */ }
  };

  const dismiss = async () => {
    try {
      await axios.post(`${API}/onboarding/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDismissed(true);
    } catch { /* ignore */ }
  };

  if (dismissed || !status || status.completed || status.dismissed) return null;

  const completedCount = STEPS.filter(s => status.steps?.[s.key]).length;
  const progress = Math.round((completedCount / STEPS.length) * 100);
  const step = STEPS[currentStep];
  const Icon = step.icon;
  const isDone = status.steps?.[step.key];

  return (
    <Card className="glass-card border border-sky-500/30 mb-6 overflow-hidden" data-testid="onboarding-wizard">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-sky-400" />
            <span className="text-white font-semibold text-sm">Guide de démarrage</span>
            <span className="text-slate-500 text-xs">{completedCount}/{STEPS.length}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={dismiss} className="text-slate-500 hover:text-white h-7 w-7 p-0"
            data-testid="dismiss-onboarding">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="px-5 pb-3">
          <Progress value={progress} className="h-1.5" />
        </div>

        {/* Steps navigation */}
        <div className="flex border-t border-white/5">
          {STEPS.map((s, i) => {
            const done = status.steps?.[s.key];
            const StIcon = s.icon;
            const active = i === currentStep;
            return (
              <button key={s.key} onClick={() => setCurrentStep(i)}
                className={`flex-1 p-3 flex flex-col items-center gap-1 transition-all border-b-2 ${
                  active ? "border-sky-500 bg-sky-500/5" : "border-transparent hover:bg-white/5"
                }`} data-testid={`onboarding-step-${s.key}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  done ? "bg-emerald-500" : active ? s.bg : "bg-slate-800"
                }`}>
                  {done ? <Check className="w-3.5 h-3.5 text-white" /> : <StIcon className={`w-3.5 h-3.5 ${active ? s.color : "text-slate-500"}`} />}
                </div>
                <span className={`text-[10px] ${active ? "text-white" : "text-slate-500"}`}>{s.title.split(" ")[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Current step content */}
        <div className="p-5 flex items-start gap-4">
          <div className={`p-3 rounded-xl ${step.bg} flex-shrink-0`}>
            <Icon className={`w-7 h-7 ${step.color}`} />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold">{step.title}</h3>
            <p className="text-slate-400 text-sm mt-1">{step.description}</p>
            <div className="flex gap-2 mt-3">
              {step.action && !isDone && (
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white"
                  onClick={() => { completeStep(step.key); navigate(step.action); }}
                  data-testid={`onboarding-action-${step.key}`}>
                  {step.actionLabel} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
              {!isDone && step.key !== "welcome" && (
                <Button size="sm" variant="outline" className="border-white/10 text-slate-400 hover:text-white"
                  onClick={() => { completeStep(step.key); setCurrentStep(Math.min(currentStep + 1, STEPS.length - 1)); }}>
                  Passer
                </Button>
              )}
              {step.key === "welcome" && !isDone && (
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white"
                  onClick={() => { completeStep("welcome"); setCurrentStep(1); }}>
                  C'est parti <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
              {isDone && (
                <div className="flex items-center gap-1.5 text-emerald-400 text-sm">
                  <Check className="w-4 h-4" /> Complété
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingWizard;
